import { SfTime } from '@app/adapters/time'
import type { TemplatePreparation, TemplateReview } from '../types'
import useI18n from '@app/shared/vue/composables/use-I18n'

const I18n = useI18n()
const DATE_FORMAT = 'DD MMM YYYY'

export const formatPreparationUser = (
  preparation: TemplatePreparation
): string => {
  const time = SfTime(preparation.time).format(DATE_FORMAT)

  return `${preparation.name} (${time})`
}

export const formatReviewUsersList = (
  reviews: TemplateReview[],
  inline = false
): string => {
  const separator = inline ? ', ' : '<br />'

  return reviews
    .map((review, index) => {
      const time = SfTime(review.time).format(DATE_FORMAT)

      return `${inline ? '' : `${index + 1}. `}${review.name} (${time})`
    })
    .join(separator)
}

export const getPreviouslyReviewedLabel = (
  reviews: TemplateReview[]
): string => {
  const currentUserId = parseInt(window.Silverfin.currentUserId())
  const currentUserWasReviewer = reviews.some(
    (review) =>
      review.user_id === currentUserId || review.userId === currentUserId
  )

  if (currentUserWasReviewer) {
    return I18n.t('reconciliation_review.previously_reviewed_by_you')
  }
  return I18n.t('reconciliation_review.previously_reviewed')
}

// Builds the two-section tooltip used by the compact prepare/review buttons:
// a bold title (the action), then an optional separator line and detail body.
export const formatCompactTooltip = (title: string, detail: string): string => {
  const titleHtml = `<strong>${title}</strong>`

  if (!detail) return titleHtml

  return `${titleHtml}<div class="tw-h-px tw-my-1.5 tw-bg-white/20"></div>${detail}`
}

export const formatReviewTooltip = (
  reviews: TemplateReview[],
  hasAnyUpToDateReview: boolean
): string => {
  const outdatedAt = reviews[0]?.outdatedAt

  const outdatedAtPrefix = outdatedAt
    ? I18n.t(
        'reconciliation_review.previously_reviewed_by_tooltip_changes_made',
        {
          date: SfTime(outdatedAt).format(DATE_FORMAT),
        }
      )
    : ''

  const prefix = hasAnyUpToDateReview
    ? I18n.t('reconciliation_review.marked_as_reviewed_by')
    : outdatedAtPrefix

  const suffix = hasAnyUpToDateReview
    ? ''
    : I18n.t('reconciliation_review.previously_reviewed_by_tooltip_check_audit')

  const users = formatReviewUsersList(reviews)

  return [prefix, users, suffix].filter(Boolean).join('<br />')
}
